/**
 * Event type for before cart change.
 * @constant {string}
 */
export const beforeCartChangeType = 'beforeCartChange';

/**
 * Event type for after cart change.
 * @constant {string}
 */
export const afterCartChangeType = 'afterCartChange';

/**
 * Retrieves the current cart information.
 *
 * @param {Object} [callbacks] - Optional callback functions.
 * @param {Function} [callbacks.beforeFetch] - Function to call before fetching the cart data.
 * @param {Function} [callbacks.afterFetch] - Function to call after successfully fetching the cart data.
 * @param {Function} [callbacks.errorCallback] - Function to call if an error occurs during the fetch.
 *
 * @returns {Promise<Object>} A promise that resolves to the cart information object.
 * @throws {Error} Will throw an error if the network request fails or if the response is not ok.
 */
export const getCart = async ({ beforeFetch, afterFetch, errorCallback } = {}) => {
  if (typeof beforeFetch !== 'undefined') await beforeFetch();
  const cart = await fetch(`${window.Shopify.routes.root}cart.js`)
    .then((res) => {
      if (!res.ok) throw new Error(`Fetch result was not ok. Requested URL: ${res.url}`);
      return res.json();
    })
    .catch(async (err) => {
      if (typeof errorCallback !== 'undefined') await errorCallback(err);
      console.error('Fetch cart info failed:', err);
      throw err;
    });
  if (typeof afterFetch !== 'undefined') await afterFetch(cart);
  return cart;
};

/**
 * Adds an item to the cart.
 *
 * @param {Object} [options] - Options for adding the item to the cart.
 * @param {HTMLFormElement} [options.form] - HTML form to send with the request.
 * @param {number} [options.id] - ID of the item to add.
 * @param {number} [options.quantity=1] - Quantity of the item to add. Defaults to `1`.
 * @param {Object} [callbacks] - Optional callback functions for different stages of the request.
 * @param {Function} [callbacks.beforeFetch] - Function to call before sending the request.
 * @param {Function} [callbacks.afterFetch] - Function to call after successfully adding the item to the cart.
 * @param {Function} [callbacks.errorCallback] - Function to call if an error occurs during the addition.
 *
 * @returns {Promise<Object>} A promise that resolves to the updated cart object.
 * @throws {Error} Will throw an error if the network request fails or if the response is not ok.
 */
export const addItemToCart = async ({
  form,
  id,
  quantity = 1,
  properties = {},
  callbacks: { beforeFetch, afterFetch, errorCallback } = {},
} = {}) => {
  if (!form && !id) {
    console.error('At least one between form or id needs to be used');
    return false;
  }
  const formData = form ? new FormData(form) : null;
  if (form && !formData?.get('id')) {
    console.error('Missing ID field from form');
    return false;
  }
  const events = {
    [`${beforeCartChangeType}`]: new CustomEvent(beforeCartChangeType),
    [`${afterCartChangeType}`]: new CustomEvent(afterCartChangeType),
  };
  document.dispatchEvent(events[beforeCartChangeType]);
  if (typeof beforeFetch !== 'undefined') await beforeFetch();

  const propRegEx = /^properties\[([\w -]+)\]$/;

  const cart = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      items: [
        form
          ? {
              id: formData.get('id'),
              quantity: parseInt(formData.get('quantity')) ?? 1,
              properties: [
                ...form.querySelectorAll(
                  'input[name^="properties["]:not([disabled]), textarea[name^="properties["]:not([disabled])',
                ),
              ].reduce((properties, i) => {
                if (!propRegEx.test(i.name)) {
                  console.warn(`Invalid line item property name: ${i.name}`);
                  return properties;
                }

                return {
                  ...properties,
                  [i.name.replace(propRegEx, '$1')]: i.value,
                };
              }, {}),
            }
          : { id, quantity, properties },
      ],
    }),
  })
    .then(async (res) => {
      if (!res.ok) {
        const error = new Error(`Fetch result was not ok. Requested URL: ${res.url}`);
        error.data = await res.json();
        throw error;
      }
      return res.json();
    })
    .catch(async (err) => {
      if (typeof errorCallback !== 'undefined') await errorCallback(err);
      console.error('Add to cart failed:', err);
      throw err;
    });
  events[afterCartChangeType].cart = cart;
  document.dispatchEvent(events[afterCartChangeType]);
  if (typeof afterFetch !== 'undefined') await afterFetch(cart);
  return cart;
};

/**
 * Clears the cart.
 *
 * @param {Object} [callbacks] - Optional callback functions for different stages of the request.
 * @param {Function} [callbacks.beforeFetch] - Function to call before sending the request.
 * @param {Function} [callbacks.afterFetch] - Function to call after successfully clearing the cart.
 * @param {Function} [callbacks.errorCallback] - Function to call if an error occurs during the request.
 *
 * @returns {Promise<Object>} A promise that resolves to the updated (cleared) cart object.
 * @throws {Error} Will throw an error if the network request fails or if the response is not ok.
 */
export const clearCart = async ({ beforeFetch, afterFetch, errorCallback } = {}) => {
  const events = {
    [`${beforeCartChangeType}`]: new CustomEvent(beforeCartChangeType),
    [`${afterCartChangeType}`]: new CustomEvent(afterCartChangeType),
  };
  document.dispatchEvent(events[beforeCartChangeType]);
  if (typeof beforeFetch !== 'undefined') await beforeFetch();
  const cart = await fetch(`${window.Shopify.routes.root}cart/clear.js`, {
    method: 'POST',
  })
    .then((res) => {
      if (!res.ok) throw new Error(`Fetch result was not ok. Requested URL: ${res.url}`);
      return res.json();
    })
    .catch(async (err) => {
      if (typeof errorCallback !== 'undefined') await errorCallback(err);
      console.error('Cart clear failed:', err);
      throw err;
    });
  events[afterCartChangeType].cart = cart;
  document.dispatchEvent(events[afterCartChangeType]);
  if (typeof afterFetch !== 'undefined') await afterFetch(cart);
  return cart;
};

/**
 * Updates the cart with new items, note, or attributes.
 *
 * @param {Object} [options] - Options for updating the cart.
 * @param {HTMLFormElement} [options.form] - HTML form to send with the request.
 * @param {Object} [options.items] - Object specifying item updates. Format: `{ item_id: new_quantity }`.
 * @param {string} [options.note] - Note to attach to the cart.
 * @param {Object} [options.attributes] - Object specifying cart attributes. Format: `{ attribute_name: attribute_value }`.
 * @param {Object} [callbacks] - Optional callback functions for different stages of the request.
 * @param {Function} [callbacks.beforeFetch] - Function to call before sending the request.
 * @param {Function} [callbacks.afterFetch] - Function to call after successfully updating the cart.
 * @param {Function} [callbacks.errorCallback] - Function to call if an error occurs during the update.
 *
 * @returns {Promise<Object>} A promise that resolves to the updated cart object.
 * @throws {Error} Will throw an error if the network request fails or if the response is not ok.
 */
export const updateCart = async ({
  form,
  items,
  note,
  attributes,
  callbacks: { beforeFetch, afterFetch, errorCallback } = {},
} = {}) => {
  if (!form && !items && !note && !attributes) {
    console.error('At least one between form or items/note/attributes needs to be used');
    return false;
  }
  const options = {
    method: 'POST',
    body: form
      ? new FormData(form)
      : JSON.stringify({
          ...(items && { updates: items }),
          ...(note && { note }),
          ...(attributes && { attributes }),
        }),
    ...(form ? {} : { headers: { 'Content-Type': 'application/json' } }),
  };
  const events = {
    [`${beforeCartChangeType}`]: new CustomEvent(beforeCartChangeType),
    [`${afterCartChangeType}`]: new CustomEvent(afterCartChangeType),
  };
  document.dispatchEvent(events[beforeCartChangeType]);
  if (typeof beforeFetch !== 'undefined') await beforeFetch();
  const cart = await fetch(`${window.Shopify.routes.root}cart/update.js`, options)
    .then((res) => {
      if (!res.ok) throw new Error(`Fetch result was not ok. Requested URL: ${res.url}`);
      return res.json();
    })
    .catch(async (err) => {
      if (typeof errorCallback !== 'undefined') await errorCallback(err);
      console.error('Cart update failed:', err);
      throw err;
    });
  events[afterCartChangeType].cart = cart;
  document.dispatchEvent(events[afterCartChangeType]);
  if (typeof afterFetch !== 'undefined') await afterFetch(cart);
  return cart;
};

/**
 * Changes the quantity or properties of an item in the cart.
 *
 * @param {Object} [options] - Options for changing the cart item.
 * @param {number} [options.line] - Line number of the item in the cart.
 * @param {number} [options.variantId] - ID of the product variant to change.
 * @param {string} [options.key] - Key of the item in the cart.
 * @param {number} [options.quantity] - New quantity for the item.
 * @param {Object} [options.properties] - New properties to set for the item.
 * @param {boolean} [options.keepOldProperties=true] - Whether to keep existing properties when updating. Defaults to `true`.
 * @param {Object} [callbacks] - Optional callback functions for different stages of the request.
 * @param {Function} [callbacks.beforeFetch] - Function to call before sending the request.
 * @param {Function} [callbacks.afterFetch] - Function to call after successfully changing the cart item.
 * @param {Function} [callbacks.errorCallback] - Function to call if an error occurs during the change.
 *
 * @returns {Promise<Object>} A promise that resolves to the updated cart object.
 * @throws {Error} Will throw an error if the network request fails or if the response is not ok.
 */
export const changeCart = async ({
  line,
  variantId,
  key,
  quantity,
  properties,
  keepOldProperties = true,
  callbacks: { beforeFetch, afterFetch, errorCallback } = {},
} = {}) => {
  if (!line && !variantId && !key) {
    console.error('At least one between line, key or variantId needs to be used');
    return false;
  }
  const oldProperties = {};
  if (!!properties && keepOldProperties) {
    const { items } = await getCart();
    const { properties } = items.find(
      ({ key: itemKey, variant_id }, index) => index + 1 == line || itemKey == key || variantId == variant_id,
    );
    Object.keys(properties).forEach((property) => (oldProperties[property] = properties[property]));
  }

  const options = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...(line ? { line } : { id: key ?? variantId }),
      ...(properties && {
        properties: Object.entries({
          ...oldProperties,
          ...properties,
        }).reduce(
          (newProperties, property) =>
            property[1] !== ''
              ? {
                  ...newProperties,
                  [property[0]]: property[1],
                }
              : newProperties,
          {},
        ),
      }),
      quantity,
    }),
  };
  const events = {
    [`${beforeCartChangeType}`]: new CustomEvent(beforeCartChangeType),
    [`${afterCartChangeType}`]: new CustomEvent(afterCartChangeType),
  };
  document.dispatchEvent(events[beforeCartChangeType]);
  if (typeof beforeFetch !== 'undefined') await beforeFetch();
  const cart = await fetch(`${window.Shopify.routes.root}cart/change.js`, options)
    .then((res) => {
      if (!res.ok) throw new Error(`Fetch result was not ok. Requested URL: ${res.url}`);
      return res.json();
    })
    .catch(async (err) => {
      if (typeof errorCallback !== 'undefined') await errorCallback(err);
      console.error('Cart change failed:', err);
      throw err;
    });
  events[afterCartChangeType].cart = cart;
  document.dispatchEvent(events[afterCartChangeType]);
  if (typeof afterFetch !== 'undefined') await afterFetch(cart);
  return cart;
};

/**
 * Removes one or more lines from the cart.
 *
 * Uses `cart/change.js` (one request per line, in sequence) instead of `cart/update.js`, because Shopify
 * emits the `product_removed_from_cart` customer event only for `change.js` requests.
 * Cart change events and callbacks are fired once for the whole removal.
 *
 * @param {Object} [options] - Options for removing the lines.
 * @param {string[]} [options.keys] - Keys of the cart lines to remove.
 * @param {Object} [callbacks] - Optional callback functions for different stages of the request.
 * @param {Function} [callbacks.beforeFetch] - Function to call before sending the requests.
 * @param {Function} [callbacks.afterFetch] - Function to call after successfully removing the lines.
 * @param {Function} [callbacks.errorCallback] - Function to call if an error occurs during the removal.
 *
 * @returns {Promise<Object>} A promise that resolves to the updated cart object.
 * @throws {Error} Will throw an error if a network request fails or if a response is not ok.
 */
export const removeFromCart = async ({
  keys = [],
  callbacks: { beforeFetch, afterFetch, errorCallback } = {},
} = {}) => {
  if (!keys.length) {
    console.error('At least one line key needs to be used');
    return false;
  }
  const events = {
    [`${beforeCartChangeType}`]: new CustomEvent(beforeCartChangeType),
    [`${afterCartChangeType}`]: new CustomEvent(afterCartChangeType),
  };
  document.dispatchEvent(events[beforeCartChangeType]);
  if (typeof beforeFetch !== 'undefined') await beforeFetch();
  let cart;
  try {
    for (const key of keys) {
      const res = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 }),
      });
      if (!res.ok) throw new Error(`Fetch result was not ok. Requested URL: ${res.url}`);
      cart = await res.json();
    }
  } catch (err) {
    if (typeof errorCallback !== 'undefined') await errorCallback(err);
    console.error('Cart remove failed:', err);
    throw err;
  }
  events[afterCartChangeType].cart = cart;
  document.dispatchEvent(events[afterCartChangeType]);
  if (typeof afterFetch !== 'undefined') await afterFetch(cart);
  return cart;
};
